import React, { useState, useMemo, useReducer, useEffect, useRef } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import cn from 'classnames';
import moment from 'moment';

import {
  PageTitle,
  Table,
  Pager,
  Tag,
  SearchBar,
  SearchFiltersSidebar,
  Shimmer,
  Button,
  FeatureEmptyGuideCard,
  Dropdown,
  FiltersDrawerHeader,
  PageIntro,
} from 'src/components';
import messages from './messages';
import { useSharesListQuery } from 'src/api/queries/shareQueries';
import { usePlanLimitsQuery } from 'src/api/queries/subscriptionQueries';
import { useDeleteShareMutation } from 'src/api/mutations/shareMutations';
import { Share, SharesListParams, getShareExpiry, getShareAssetCount, getShareTitle } from 'src/types/TShare';
import { getPurposeLabel } from 'src/components/ShareSettingsForm';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import { AssetGetLink, Eye, Trash, Faders, DotsThreeVertical } from 'src/common/icons';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { config } from 'src/utils/config';
import { TSorting } from 'src/types/TSorting';
import useFiltersDrawer from 'src/hooks/useFiltersDrawer';

import styles from './styles.module.scss';

const initialSearchParams: SharesListParams = {
  sort_by: ['expiry_date DESC'],
  offset: 0,
  limit: 30,
};

const searchParamsReducer = (state: SharesListParams, payload: Partial<SharesListParams>) => ({
  ...state,
  ...payload,
});

export const SharesList: React.FC = () => {
  // Live-count plan cap (rollout 28). quota 0 == unlimited. Shares are
  // created from asset selection (not this page), so the header carries the
  // counter and the share modal's submit surfaces the gate's upgrade error.
  const { data: planLimits } = usePlanLimitsQuery();
  const sharesLimit = planLimits?.shares;
  const atSharesCap = Boolean(
    sharesLimit && sharesLimit.quota > 0 && sharesLimit.used >= sharesLimit.quota,
  );
  const intl = useIntl();
  const history = useHistory();
  const { showAlert, showSuccess } = useAlerts();
  const { getErrorMessage } = useErrorMessages();
  const [itemsPerPage, setItemsPerPage] = useLocalStorage('shares.itemsPerPage', '30');
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sorting, setSorting] = useState<TSorting>(['expiry_date', 'DESC']);
  const [filtersVisible, setFiltersVisible] = useState(false);

  const filtersSidebarRef = useRef<HTMLDivElement>(null);
  const filtersDrawer = useFiltersDrawer({ visible: filtersVisible, setVisible: setFiltersVisible });

  const [searchParams, dispatchSearchParams] = useReducer(searchParamsReducer, initialSearchParams, (state) => {
    const limit = Number(itemsPerPage);
    return { ...state, limit };
  });

  const currentPage = useMemo(() => {
    if (!searchParams.offset || !searchParams.limit) return 1;
    return Math.floor(searchParams.offset / searchParams.limit) + 1;
  }, [searchParams]);

  const {
    data: sharesResult,
    isLoading,
    isRefetching,
    refetch,
  } = useSharesListQuery(searchParams, {
    onSuccess: (results) => {
      const total = results?.data?.total_shares ?? results?.data?.shares_found ?? 0;
      if (total > 0) {
        setTotalPages(Math.ceil(total / (searchParams.limit ?? 30)));
      }
    },
  });

  const deleteShareMutation = useDeleteShareMutation({
    onSuccess: () => {
      showSuccess('Share deleted successfully');
      refetch();
    },
    onError: (error) => {
      showAlert(getErrorMessage(error.message));
    },
  });

  const handleSearchShares = (term: string) => {
    setSearchTerm(term);
    dispatchSearchParams({
      keyword: term || undefined,
      offset: 0,
    });
  };

  const handleApplyFilters = (filters: any) => {
    const newParams: Partial<SharesListParams> = {
      offset: 0,
    };

    if (filters.status) {
      newParams.status = filters.status;
    } else {
      newParams.status = undefined;
    }

    if (filters.shared_with) {
      newParams.shared_with = filters.shared_with;
    } else {
      newParams.shared_with = undefined;
    }

    if (filters.approval_summary) {
      newParams.approval_summary = filters.approval_summary;
    } else {
      newParams.approval_summary = undefined;
    }

    if (filters.expiry_date_range) {
      newParams.start_date = filters.expiry_date_range.start || undefined;
      newParams.end_date = filters.expiry_date_range.end || undefined;
    } else {
      newParams.start_date = undefined;
      newParams.end_date = undefined;
    }

    dispatchSearchParams(newParams);
    setFiltersVisible(false);
  };

  const handlePageChange = (page: number) => {
    dispatchSearchParams({
      offset: (page - 1) * (searchParams.limit ?? 30),
    });
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setItemsPerPage(newLimit.toString());
    dispatchSearchParams({
      limit: newLimit,
      offset: 0,
    });
  };

  const handleViewShare = (shareId: string) => {
    history.push(`/shares/${shareId}`);
  };

  const handleCopyLink = async (share: Share) => {
    try {
      const shareUrl = share.url || `${config.hostUrl}/shared/${share.id}`;
      await navigator.clipboard.writeText(shareUrl);
      showSuccess('Share link copied to clipboard');
    } catch (err) {
      showAlert('Failed to copy link');
    }
  };

  const handleDeleteShare = (shareId: string) => {
    if (window.confirm('Are you sure you want to delete this share?')) {
      deleteShareMutation.mutate(shareId);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    dispatchSearchParams({
      keyword: undefined,
      offset: 0,
    });
  };

  const handleClearFilter = (filterKey: string) => {
    switch (filterKey) {
      case 'status':
        dispatchSearchParams({ status: undefined });
        break;
      case 'shared_with':
        dispatchSearchParams({ shared_with: undefined });
        break;
      case 'approval_summary':
        dispatchSearchParams({ approval_summary: undefined });
        break;
      case 'start_date':
        dispatchSearchParams({ start_date: undefined });
        break;
      case 'end_date':
        dispatchSearchParams({ end_date: undefined });
        break;
    }
  };

  useEffect(() => {
    dispatchSearchParams({
      sort_by: [sorting.join(' ')],
    });
  }, [sorting]);

  const getStatusClass = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'OK':
      case 'ACTIVE':
        return styles.active;
      case 'EXPIRED':
        return styles.expired;
      case 'DISABLED':
        return styles.disabled;
      default:
        return '';
    }
  };

  const formatExpiryDate = (share: Share): string => {
    const expiry = getShareExpiry(share);
    if (!expiry) return '-';
    return moment.unix(expiry).format('MMM D, YYYY');
  };

  const formatCreatedDate = (share: Share): string => {
    if (!share.created_at) return '-';
    if (typeof share.created_at === 'number') {
      return moment.unix(share.created_at).format('MMM D, YYYY');
    }
    return moment.utc(share.created_at).local().format('MMM D, YYYY');
  };

  const getDisplayStatus = (status: string): string => {
    if (status?.toUpperCase() === 'OK') return 'Active';
    return status || '-';
  };

  const shares = sharesResult?.data?.shares ?? [];
  const totalShares = sharesResult?.data?.total_shares ?? sharesResult?.data?.shares_found ?? 0;

  const hasActiveFilters = !!(
    searchParams.keyword ||
    searchParams.status ||
    searchParams.shared_with ||
    searchParams.approval_summary ||
    searchParams.start_date ||
    searchParams.end_date
  );

  return (
    <div className={styles.pageWrapper}>
      <PageTitle
        title="Shares"
        className={styles.pageTitle}
        counter={totalShares}
        rightContainer={
          <Button
            size="small"
            mode="tertiary"
            icon={<Faders />}
            onClick={() => history.push('/shares/templates')}
          >
            Manage Share Templates
          </Button>
        }
      />
      <PageIntro
        inset
        overview={(
          <>
            Branded pages for distributing assets to external teams, partners and
            clients. Quick to create, access-controlled, and revocable at any time.
          </>
        )}
      />
      {/* Plan-cap notice (rollout 28) — shown only AT the cap; new shares
          are created from asset selection, and that path is refused there. */}
      {atSharesCap && sharesLimit && (
        <div className={styles.limitBanner} data-test="shares-limit-banner" role="status">
          You&apos;ve reached your plan&apos;s limit of {sharesLimit.quota} share pages.
          Delete shares you no longer need, or{' '}
          <Link to="/admin/subscription/plans">upgrade your plan</Link> for more.
        </div>
      )}

      <SearchBar
        search={searchTerm}
        sortKeys={[
          { key: 'expiry_date', value: 'Expiry Date' },
          { key: 'title', value: 'Title' },
          { key: 'created_at', value: 'Created Date' },
        ]}
        onSelectSort={setSorting}
        searchButton
        onClickSearchButton={handleSearchShares}
        onClickFiltersButton={() => setFiltersVisible(!filtersVisible)}
        hideFaceSearchButton={true}
      />

      <section className={styles.pageContainer}>
        {filtersDrawer.drawerOpen && (
          <div className={styles.mobileScrim} onClick={filtersDrawer.closeIfMobile} aria-hidden />
        )}
        {filtersVisible && (
          <div ref={filtersSidebarRef} className={styles.filterSidebarWrapper}>
            <FiltersDrawerHeader onClose={filtersDrawer.closeIfMobile} />
            <SearchFiltersSidebar
              onApplyFilters={handleApplyFilters}
              filters={[
                {
                  title: 'Status',
                  key: 'status',
                  inputs: {
                    type: 'select',
                    label: 'Share Status',
                    initialValue: searchParams.status || '',
                    options: [
                      { label: 'All', value: '' },
                      { label: 'Active', value: 'OK' },
                      { label: 'Expired', value: 'EXPIRED' },
                    ],
                  },
                },
                {
                  title: 'Shared With',
                  key: 'shared_with',
                  inputs: {
                    type: 'text',
                    label: 'Email Address',
                    initialValue: searchParams.shared_with,
                    placeholder: 'reviewer@example.com',
                  },
                },
                {
                  title: 'Approval Status',
                  key: 'approval_summary',
                  inputs: {
                    type: 'select',
                    label: 'Approval Summary',
                    initialValue: searchParams.approval_summary || '',
                    options: [
                      { label: 'All', value: '' },
                      { label: 'Has Pending', value: 'has_pending' },
                      { label: 'All Approved', value: 'all_approved' },
                      { label: 'Has Rejected', value: 'has_rejected' },
                    ],
                  },
                },
                {
                  title: 'Expiry Date Range',
                  key: 'expiry_date_range',
                  inputs: [
                    {
                      key: 'start',
                      type: 'date',
                      label: 'Start Date:',
                      initialValue: searchParams.start_date,
                    },
                    {
                      key: 'end',
                      type: 'date',
                      label: 'End Date:',
                      initialValue: searchParams.end_date,
                    },
                  ],
                },
              ]}
            />
          </div>
        )}

        <div className={cn([styles.pageContent])}>
          {/* Active Filters Tags */}
          {hasActiveFilters && (
            <div className={styles.actionsWrapper}>
              <div className={styles.tags}>
                {searchParams.keyword && (
                  <Tag text={`Search: ${searchParams.keyword}`} onClose={handleClearSearch} />
                )}
                {searchParams.status && (
                  <Tag
                    text={`Status: ${searchParams.status === 'OK' ? 'Active' : searchParams.status}`}
                    onClose={() => handleClearFilter('status')}
                  />
                )}
                {searchParams.shared_with && (
                  <Tag
                    text={`Shared with: ${searchParams.shared_with}`}
                    onClose={() => handleClearFilter('shared_with')}
                  />
                )}
                {searchParams.approval_summary && (
                  <Tag
                    text={`Approvals: ${
                      searchParams.approval_summary === 'has_pending' ? 'Has Pending' :
                      searchParams.approval_summary === 'all_approved' ? 'All Approved' :
                      searchParams.approval_summary === 'has_rejected' ? 'Has Rejected' :
                      searchParams.approval_summary
                    }`}
                    onClose={() => handleClearFilter('approval_summary')}
                  />
                )}
                {searchParams.start_date && (
                  <Tag
                    text={`Expiry from: ${searchParams.start_date}`}
                    onClose={() => handleClearFilter('start_date')}
                  />
                )}
                {searchParams.end_date && (
                  <Tag
                    text={`Expiry to: ${searchParams.end_date}`}
                    onClose={() => handleClearFilter('end_date')}
                  />
                )}
              </div>
            </div>
          )}

          {isLoading ? (
            <div className={styles.loadingWrapper}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Shimmer key={i} w="100%" h="4rem" radius="0.25rem" />
              ))}
            </div>
          ) : shares.length === 0 ? (
            hasActiveFilters ? (
              <div className={styles.emptyState}>
                <h3>
                  <FormattedMessage {...messages.emptyFilteredTitle} />
                </h3>
                <p>
                  <FormattedMessage {...messages.emptyFilteredDescription} />
                </p>
              </div>
            ) : (
              <FeatureEmptyGuideCard
                title="No shares created"
                info={<FormattedMessage {...messages.emptyGuideStep1} />}
                mediaStep={{
                  src: '/userhelp/create-share.jpg',
                  alt: intl.formatMessage(messages.emptyGuideImageAlt),
                  body: (
                    <FormattedMessage
                      {...messages.emptyGuideStep2}
                      values={{
                        assetsPage: <Link to="/assets">Assets page</Link>,
                        withSelected: <strong>With Selected</strong>,
                        share: <strong>Share</strong>,
                      }}
                    />
                  ),
                }}
              />
            )
          ) : (
            <>
              {/* Desktop: tabular layout. Mobile: hidden via CSS in styles.module.scss
                  (the card list below renders instead). Keeping both in the DOM lets
                  CSS decide and avoids re-mounting on resize. */}
              <div className={styles.tableWrapper}>
                <Table
                  headers={['Title', 'Purpose', 'Status', 'Assets', 'Created', 'Expiry', 'Actions']}
                  className={styles.sharesTable}
                >
                  {shares.map((share: Share) => (
                    <tr key={share.id}>
                      <td>
                        <span
                          className={styles.shareTitle}
                          onClick={() => handleViewShare(share.id)}
                        >
                          {getShareTitle(share)}
                        </span>
                      </td>
                      <td>
                        {getPurposeLabel(share.preset_id) ? (
                          <span className={styles.purposeBadge}>
                            {getPurposeLabel(share.preset_id)}
                          </span>
                        ) : (
                          <span className={styles.purposeBadgeMuted}>Custom</span>
                        )}
                      </td>
                      <td>
                        <span className={cn(styles.shareStatus, getStatusClass(share.status))}>
                          {getDisplayStatus(share.status)}
                        </span>
                      </td>
                      <td>
                        <span className={styles.assetCount}>{getShareAssetCount(share)}</span>
                      </td>
                      <td>
                        <span className={styles.dateText}>{formatCreatedDate(share)}</span>
                      </td>
                      <td>
                        <span className={styles.dateText}>{formatExpiryDate(share)}</span>
                      </td>
                      <td>
                        <div className={styles.actionButtons}>
                          <button
                            className={styles.actionButton}
                            onClick={() => handleViewShare(share.id)}
                            title="View share"
                          >
                            <Eye />
                          </button>
                          <button
                            className={styles.actionButton}
                            onClick={() => handleCopyLink(share)}
                            title="Copy link"
                          >
                            <AssetGetLink />
                          </button>
                          <button
                            className={cn(styles.actionButton, styles.delete)}
                            onClick={() => handleDeleteShare(share.id)}
                            title="Delete share"
                          >
                            <Trash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </Table>
              </div>

              {/* Mobile-only card layout. Visibility is governed by CSS so it
                  stays a no-op on desktop. */}
              <ul className={styles.shareCardList}>
                {shares.map((share: Share) => (
                  <li key={share.id} className={styles.shareCard}>
                    <div className={styles.shareCardHeader}>
                      <button
                        type="button"
                        className={styles.shareCardTitle}
                        onClick={() => handleViewShare(share.id)}
                      >
                        {getShareTitle(share)}
                      </button>
                      <div className={styles.shareCardMenu}>
                        <Dropdown
                          variant="menu"
                          dropdownAnchor="right"
                          icon={<DotsThreeVertical />}
                          options={[
                            { key: 'view', value: 'View share', icon: <Eye /> },
                            { key: 'copy', value: 'Copy link', icon: <AssetGetLink /> },
                            { key: 'delete', value: 'Delete share', icon: <Trash /> },
                          ]}
                          onClick={(action) => {
                            if (action === 'view') handleViewShare(share.id);
                            else if (action === 'copy') handleCopyLink(share);
                            else if (action === 'delete') handleDeleteShare(share.id);
                          }}
                        />
                      </div>
                    </div>
                    <div className={styles.shareCardMeta}>
                      <span className={styles.shareCardMetaItem}>
                        <span className={cn(styles.shareStatus, getStatusClass(share.status))}>
                          {getDisplayStatus(share.status)}
                        </span>
                      </span>
                      {getPurposeLabel(share.preset_id) && (
                        <span className={styles.shareCardMetaItem}>
                          <span className={styles.purposeBadge}>
                            {getPurposeLabel(share.preset_id)}
                          </span>
                        </span>
                      )}
                      <span className={styles.shareCardMetaItem}>
                        <strong>{getShareAssetCount(share)}</strong> assets
                      </span>
                      <span className={styles.shareCardMetaItem}>
                        Expires <strong>{formatExpiryDate(share)}</strong>
                      </span>
                      <span className={styles.shareCardMetaItem}>
                        Created {formatCreatedDate(share)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>

              {totalShares > Number(itemsPerPage) && (
                <div className={styles.pagerWrapper}>
                  <Pager
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onChange={handlePageChange}
                    itemsPerPage={Number(itemsPerPage)}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    showItemsPerPage={true}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default SharesList;
